import type { SpeechRequest } from '@/api/types';
import { MAX_SHARED_SPEECH_BYTES } from '@/connection/synchronizedSpeech';

export type SpeechSynthesizer = (request: SpeechRequest, signal?: AbortSignal) => Promise<ArrayBuffer>;
type Outcome = { audio: ArrayBuffer; error?: never } | { audio?: never; error: unknown };
type Entry = {
  request: SpeechRequest;
  promise: Promise<Outcome>;
  resolve: (result: Outcome) => void;
  state: 'queued' | 'loading' | 'ready';
  bytes: number;
  used: boolean;
};

export const speechRequestKey = (request: SpeechRequest) => JSON.stringify([
  request.text, request.speaker ?? 3, request.speedScale ?? 1, request.pitchScale ?? 0,
]);
const MAX_CACHE_BYTES = 32 * 1024 * 1024;

export class SpeechPrefetch {
  private readonly controller = new AbortController();
  private readonly entries = new Map<string, Entry>();
  private readonly queue: Entry[] = [];
  private bytes = 0;
  private running = false;
  private closed = false;
  private readonly abort = () => this.dispose();

  constructor(
    private readonly synthesize: SpeechSynthesizer,
    private readonly reportError: (message: string) => void,
    private readonly signal?: AbortSignal,
  ) {
    signal?.addEventListener('abort', this.abort, { once: true });
    if (signal?.aborted) this.dispose();
  }

  async start(requests: readonly SpeechRequest[]): Promise<void> {
    this.ensureOpen();
    const entries = requests.map((request) => this.enqueue(request));
    this.pump();
    if (entries[0]) this.unwrap(await entries[0].promise);
    this.ensureOpen();
  }

  async get(request: SpeechRequest): Promise<ArrayBuffer> {
    this.ensureOpen();
    const entry = this.enqueue(request);
    entry.used = true;
    if (entry.state === 'queued') {
      const index = this.queue.indexOf(entry);
      this.queue.splice(index, 1);
      this.queue.unshift(entry);
    }
    this.pump();
    const result = await entry.promise;
    this.ensureOpen();
    this.pump();
    return this.unwrap(result);
  }

  dispose() {
    if (this.closed) return;
    this.closed = true;
    this.controller.abort();
    this.signal?.removeEventListener('abort', this.abort);
    const error = new DOMException('Flow execution aborted', 'AbortError');
    this.entries.forEach((entry) => entry.resolve({ error }));
    this.queue.length = 0;
    this.entries.clear();
    this.bytes = 0;
  }

  private ensureOpen() {
    if (this.closed) throw new DOMException('Flow execution aborted', 'AbortError');
  }

  private unwrap(result: Outcome): ArrayBuffer {
    if (result.audio) return result.audio;
    throw result.error;
  }

  private enqueue(request: SpeechRequest): Entry {
    const key = speechRequestKey(request);
    const existing = this.entries.get(key);
    if (existing) return existing;
    let resolve!: Entry['resolve'];
    const promise = new Promise<Outcome>((done) => { resolve = done; });
    const entry: Entry = { request, promise, resolve, state: 'queued', bytes: 0, used: false };
    this.entries.set(key, entry);
    this.queue.push(entry);
    return entry;
  }

  private pump() {
    if (this.running || this.closed) return;
    const next = this.queue[0];
    if (!next) return;
    // Reserve room for a maximum-size response; pause lookahead instead of evicting upcoming speech.
    while (this.bytes + MAX_SHARED_SPEECH_BYTES > MAX_CACHE_BYTES) {
      const victim = [...this.entries.entries()].find(([, entry]) => entry !== next && entry.state === 'ready' && entry.used)
        ?? (next.used
          ? [...this.entries.entries()].reverse().find(([, entry]) => entry !== next && entry.state === 'ready')
          : undefined);
      if (!victim) return;
      this.entries.delete(victim[0]);
      this.bytes -= victim[1].bytes;
    }
    this.queue.shift();
    next.state = 'loading';
    this.running = true;
    void this.load(next);
  }

  private async load(entry: Entry) {
    try {
      const audio = await this.synthesize(entry.request, this.controller.signal);
      this.ensureOpen();
      if (audio.byteLength === 0 || audio.byteLength > MAX_SHARED_SPEECH_BYTES) {
        throw new Error('TTS音声サイズが範囲外です（1バイト〜5MiB）');
      }
      entry.bytes = audio.byteLength;
      this.bytes += audio.byteLength;
      entry.resolve({ audio });
    } catch (error) {
      if (!this.closed) this.reportError(`[TTS先読み] 取得失敗: ${error instanceof Error ? error.message : String(error)}`);
      entry.resolve({ error });
    } finally {
      entry.state = 'ready';
      this.running = false;
      this.pump();
    }
  }
}
